/**
 * Parses a hex color string to RGB values.
 * Supports #rgb, #rrggbb, #rgba, #rrggbbaa formats.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
  }
  const short = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
  if (short) {
    return {
      r: parseInt(short[1] + short[1], 16),
      g: parseInt(short[2] + short[2], 16),
      b: parseInt(short[3] + short[3], 16),
    };
  }
  return null;
}

/**
 * Calculates relative luminance per WCAG 2.1.
 * Returns a value between 0 (black) and 1 (white).
 */
function getLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Returns "white" or "black" for optimal text contrast on the given background color.
 * Uses WCAG 2.1 relative luminance with a threshold of 0.5.
 */
export function getContrastTextColor(backgroundColor: string): "white" | "black" {
  if (!backgroundColor || typeof backgroundColor !== "string") return "black";
  const rgb = hexToRgb(backgroundColor.trim());
  if (!rgb) return "black";
  const luminance = getLuminance(rgb);
  return luminance > 0.5 ? "black" : "white";
}
